import { useCallback, useEffect, useState, useContext } from "react";
import axios from "../api/axios";
import { AuthContext } from "../context/AuthContext";

const fields = [
  { name: "name", label: "Full name", required: true },
  { name: "mobile", label: "Mobile number", required: true, inputMode: "numeric", maxLength: 10, pattern: "\\d{10}" },
  { name: "email", label: "Email address", type: "email", maxLength: 254 },
  { name: "dateOfBirth", label: "Date of birth", type: "date" },
  { name: "address", label: "Address", wide: true, maxLength: 500 },
  { name: "state", label: "State", maxLength: 100 },
  { name: "district", label: "District", maxLength: 100 },
  { name: "tahsil", label: "Tahsil", maxLength: 100 },
  { name: "postOffice", label: "Post office", maxLength: 100 },
  { name: "pinCode", label: "PIN code", inputMode: "numeric", maxLength: 6, pattern: "\\d{6}" },
  { name: "nomineeName", label: "Nominee name", maxLength: 100 },
  { name: "nomineeRelation", label: "Nominee relation", maxLength: 50 },
  { name: "nomineePhone", label: "Nominee phone", inputMode: "numeric", maxLength: 10, pattern: "\\d{10}" },
];

const emptyProfile = Object.fromEntries(fields.map(({ name }) => [name, ""]));

const getInputValue = (field, value) => {
  if (field === "dateOfBirth" && value) return new Date(value).toISOString().slice(0, 10);
  return value || "";
};

const styles = `
  @import url('https://fonts.googleapis.com/css2?family=Montserrat:wght@300;400;500;600;700;800;900&display=swap');

  .puf-root {
    font-family: 'Montserrat', sans-serif;
  }

  /* Card */
  .puf-card {
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    border-radius: 16px;
    padding: 24px;
    box-shadow: 10px 10px 20px #8f95a0, -5px -5px 12px #ffffff;
    margin-bottom: 24px;
  }

  .puf-card-title {
    display: flex;
    align-items: center;
    gap: 10px;
    font-size: 16px;
    font-weight: 800;
    background: linear-gradient(135deg, #2563eb, #8b5cf6);
    -webkit-background-clip: text;
    -webkit-text-fill-color: transparent;
    background-clip: text;
    text-transform: uppercase;
    letter-spacing: 0.08em;
    margin-bottom: 12px;
  }

  .puf-card-title-bar {
    width: 4px;
    height: 18px;
    border-radius: 99px;
    background: linear-gradient(180deg, #fbbf24, #f59e0b);
    box-shadow: 0 2px 6px rgba(245, 158, 11, 0.4);
    flex-shrink: 0;
  }

  .puf-description {
    font-size: 14px;
    color: #64748b;
    margin-bottom: 16px;
    font-weight: 500;
  }

  /* Alerts */
  .puf-alert-error {
    margin-bottom: 16px;
    border-radius: 10px;
    border: 1px solid #fecaca;
    background: #fef2f2;
    padding: 12px;
    font-size: 14px;
    color: #b91c1c;
    font-weight: 500;
  }

  .puf-alert-success {
    margin-bottom: 16px;
    border-radius: 10px;
    border: 1px solid #86efac;
    background: #f0fdf4;
    padding: 12px;
    font-size: 14px;
    color: #15803d;
    font-weight: 500;
  }

  /* Action Row */
  .puf-action-row {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
  }

  .puf-btn-primary {
    border-radius: 10px;
    background: linear-gradient(135deg, #2563eb, #3b82f6);
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 700;
    color: #ffffff;
    border: none;
    cursor: pointer;
    transition: all 0.3s ease;
    font-family: 'Montserrat', sans-serif;
    box-shadow: 0 4px 12px rgba(37, 99, 235, 0.3);
  }

  .puf-btn-primary:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 6px 18px rgba(37, 99, 235, 0.4);
  }

  .puf-btn-primary:disabled {
    cursor: not-allowed;
    background: linear-gradient(135deg, #94a3b8, #cbd5e1);
    box-shadow: none;
  }

  .puf-pending-note {
    font-size: 14px;
    color: #b45309;
    font-weight: 600;
  }

  /* Modal */
  .puf-modal-overlay {
    position: fixed;
    inset: 0;
    z-index: 50;
    display: flex;
    align-items: center;
    justify-content: center;
    background: rgba(0, 0, 0, 0.5);
    backdrop-filter: blur(4px);
    padding: 16px;
  }

  .puf-modal {
    max-height: 90vh;
    width: 100%;
    max-width: 42rem;
    overflow-y: auto;
    border-radius: 16px;
    background: linear-gradient(145deg, #e8edf5, #dce1e8);
    padding: 24px;
    box-shadow: 16px 16px 32px #8f95a0, -8px -8px 16px #ffffff;
  }

  .puf-modal::-webkit-scrollbar {
    width: 6px;
  }

  .puf-modal::-webkit-scrollbar-track {
    background: #d1d6df;
    border-radius: 99px;
  }

  .puf-modal::-webkit-scrollbar-thumb {
    background: linear-gradient(135deg, #2563eb, #8b5cf6);
    border-radius: 99px;
  }

  .puf-modal-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .puf-modal-title {
    font-size: 16px;
    font-weight: 800;
    color: #1e293b;
  }

  .puf-modal-close {
    font-size: 26px;
    line-height: 1;
    color: #64748b;
    background: none;
    border: none;
    cursor: pointer;
    transition: color 0.2s;
    font-family: 'Montserrat', sans-serif;
  }

  .puf-modal-close:hover {
    color: #1e293b;
  }

  /* Form */
  .puf-form-grid {
    display: grid;
    grid-template-columns: 1fr;
    gap: 12px;
  }

  @media (min-width: 640px) {
    .puf-form-grid {
      grid-template-columns: 1fr 1fr;
    }
  }

  .puf-form-label {
    display: block;
    font-size: 13px;
    font-weight: 600;
    color: #374151;
  }

  .puf-form-label-wide {
    grid-column: 1 / -1;
  }

  .puf-form-input {
    margin-top: 4px;
    width: 100%;
    border-radius: 10px;
    border: 1px solid #cbd5e1;
    background: #ffffff;
    padding: 8px 12px;
    font-size: 14px;
    font-family: 'Montserrat', sans-serif;
    outline: none;
    transition: all 0.2s;
    box-sizing: border-box;
  }

  .puf-form-input:focus {
    border-color: #3b82f6;
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.15);
  }

  .puf-form-input:disabled {
    background: #f1f5f9;
    cursor: not-allowed;
  }

  .puf-form-actions {
    margin-top: 16px;
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 12px;
  }

  .puf-btn-secondary {
    border-radius: 10px;
    border: 1px solid #cbd5e1;
    background: #ffffff;
    padding: 10px 20px;
    font-size: 14px;
    font-weight: 700;
    color: #334155;
    cursor: pointer;
    transition: background 0.2s;
    font-family: 'Montserrat', sans-serif;
  }

  .puf-btn-secondary:hover {
    background: #f8fafc;
  }

  /* History */
  .puf-history {
    margin-top: 24px;
    border-top: 1px solid #cbd5e1;
    padding-top: 16px;
  }

  .puf-history-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 12px;
  }

  .puf-history-title {
    font-size: 14px;
    font-weight: 800;
    color: #1e293b;
  }

  .puf-refresh-btn {
    font-size: 14px;
    font-weight: 700;
    color: #2563eb;
    background: none;
    border: none;
    cursor: pointer;
    transition: color 0.2s;
    font-family: 'Montserrat', sans-serif;
  }

  .puf-refresh-btn:hover {
    color: #1e3a8a;
  }

  .puf-no-requests {
    font-size: 14px;
    color: #64748b;
    font-weight: 500;
  }

  .puf-requests-list {
    display: flex;
    flex-direction: column;
    gap: 12px;
  }

  .puf-request-card {
    border-radius: 12px;
    border: 1px solid #cbd5e1;
    background: #f8fafc;
    padding: 12px;
  }

  .puf-request-header {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
  }

  .puf-status-badge {
    border-radius: 9999px;
    padding: 4px 10px;
    font-size: 12px;
    font-weight: 800;
    text-transform: capitalize;
  }

  .puf-status-approved {
    background: #d1fae5;
    color: #065f46;
  }

  .puf-status-rejected {
    background: #fee2e2;
    color: #991b1b;
  }

  .puf-status-pending {
    background: #fef3c7;
    color: #92400e;
  }

  .puf-request-time {
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
  }

  .puf-changes-list {
    margin-top: 8px;
    display: flex;
    flex-direction: column;
    gap: 4px;
    font-size: 14px;
    color: #334155;
    list-style: none;
    padding: 0;
  }

  .puf-change-label {
    font-weight: 600;
  }

  .puf-rejection-reason {
    margin-top: 8px;
    border-radius: 8px;
    background: #fef2f2;
    padding: 8px;
    font-size: 14px;
    color: #991b1b;
  }

  .puf-reviewed-time {
    margin-top: 8px;
    font-size: 12px;
    color: #64748b;
    font-weight: 500;
  }

  /* Responsive */
  @media (max-width: 640px) {
    .puf-card {
      padding: 16px;
    }

    .puf-modal {
      padding: 16px;
      margin-bottom: 34px;
    }

    .puf-form-grid {
      grid-template-columns: 1fr;
    }

    .puf-btn-primary,
    .puf-btn-secondary {
      width: 100%;
      text-align: center;
    }
  }
`;

const ProfileUpdateForm = () => {
  const { updateUser } = useContext(AuthContext);
  const [profile, setProfile] = useState(emptyProfile);
  const [initialProfile, setInitialProfile] = useState(emptyProfile);
  const [allowedFields, setAllowedFields] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [open, setOpen] = useState(false);

  const loadRequests = useCallback(async () => {
    setError("");
    try {
      const { data } = await axios.get("/profile-updates/mine");
      const currentProfile = { ...emptyProfile };
      fields.forEach(({ name }) => {
        currentProfile[name] = getInputValue(name, data.profile?.[name]);
      });
      setProfile(currentProfile);
      setInitialProfile(currentProfile);
      setRequests(data.requests || []);
      setAllowedFields(data.allowedFields || []);
      updateUser(data.profile || {});
    } catch (loadError) {
      setError(loadError.response?.data?.message || "Unable to load profile update requests.");
    } finally {
      setLoading(false);
    }
  }, [updateUser]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  const hasPendingRequest = requests.some((request) => request.status === "pending");
  const editableFieldList = fields.filter((field) => allowedFields.includes(field.name));

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setNotice("");
    setSubmitting(true);
    try {
      const changes = Object.fromEntries(
        Object.entries(profile).filter(([field, value]) =>
          allowedFields.includes(field) && value !== initialProfile[field])
      );
      if (!Object.keys(changes).length) {
        setError("Change at least one field before submitting.");
        return;
      }
      await axios.post("/profile-updates/mine", { changes });
      setNotice("Your profile update request was sent to the admin for review.");
      await loadRequests();
      setOpen(false);
    } catch (submitError) {
      setError(submitError.response?.data?.message || "Unable to submit your profile update request.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section className="puf-root puf-card" aria-labelledby="profile-update-heading">
      <style>{styles}</style>

      <div className="puf-card-title">
        <div className="puf-card-title-bar" />
        <span id="profile-update-heading">Request Profile Update</span>
      </div>
      <p className="puf-description">
        Changes are applied only after an administrator approves your request.
      </p>

      {error && (
        <div role="alert" className="puf-alert-error">{error}</div>
      )}
      {notice && (
        <div role="status" className="puf-alert-success">{notice}</div>
      )}

      <div className="puf-action-row">
        <button
          type="button"
          onClick={() => { setError(""); setNotice(""); setOpen(true); }}
          disabled={loading || hasPendingRequest || !editableFieldList.length}
          className="puf-btn-primary"
        >
          Update Profile
        </button>
        {!loading && !editableFieldList.length && !hasPendingRequest && (
          <span className="puf-pending-note">
            No profile fields are currently open for editing. Contact the admin to request access.
          </span>
        )}
        {hasPendingRequest && (
          <span className="puf-pending-note">
            A request is already awaiting admin review.
          </span>
        )}
      </div>

      {open && !loading && (
        <div className="puf-modal-overlay" role="dialog" aria-modal="true">
          <div className="puf-modal">
            <div className="puf-modal-header">
              <h3 className="puf-modal-title">Update Profile</h3>
              <button
                type="button"
                onClick={() => setOpen(false)}
                aria-label="Close"
                className="puf-modal-close"
              >
                ×
              </button>
            </div>
            {error && (
              <div role="alert" className="puf-alert-error">{error}</div>
            )}
            <form onSubmit={handleSubmit}>
              <div className="puf-form-grid">
                {editableFieldList.map((field) => (
                  <label
                    key={field.name}
                    className={`puf-form-label ${field.wide ? "puf-form-label-wide" : ""}`}
                  >
                    {field.label}
                    <input
                      type={field.type || "text"}
                      name={field.name}
                      value={profile[field.name]}
                      onChange={(event) => setProfile((current) => ({ ...current, [field.name]: event.target.value }))}
                      required={field.required}
                      inputMode={field.inputMode}
                      maxLength={field.maxLength}
                      pattern={field.pattern}
                      minLength={field.name === "name" ? 2 : undefined}
                      className="puf-form-input"
                      disabled={hasPendingRequest || submitting}
                    />
                  </label>
                ))}
              </div>
              <div className="puf-form-actions">
                <button
                  type="submit"
                  disabled={hasPendingRequest || submitting}
                  className="puf-btn-primary"
                >
                  {submitting ? "Submitting…" : "Submit for approval"}
                </button>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="puf-btn-secondary"
                >
                  Cancel
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      <div className="puf-history">
        <div className="puf-history-header">
          <h3 className="puf-history-title">Request history</h3>
          <button
            type="button"
            onClick={loadRequests}
            className="puf-refresh-btn"
          >
            Refresh status
          </button>
        </div>
        {requests.length === 0 ? (
          <p className="puf-no-requests">
            You have not submitted a profile update request.
          </p>
        ) : (
          <div className="puf-requests-list">
            {requests.map((request) => (
              <article key={request._id} className="puf-request-card">
                <div className="puf-request-header">
                  <span className={`puf-status-badge ${
                    request.status === "approved" ? "puf-status-approved" :
                    request.status === "rejected" ? "puf-status-rejected" : "puf-status-pending"
                  }`}>
                    {request.status}
                  </span>
                  <time className="puf-request-time" dateTime={request.createdAt}>
                    Submitted {new Date(request.createdAt).toLocaleString()}
                  </time>
                </div>
                <ul className="puf-changes-list">
                  {Object.entries(request.requestedChanges || {}).filter(([key]) => key !== "_id").map(([key, value]) => (
                    <li key={key}>
                      <span className="puf-change-label">
                        {fields.find((field) => field.name === key)?.label || key}:
                      </span>{" "}
                      {value ? (key === "dateOfBirth" ? new Date(value).toLocaleDateString() : String(value)) : "—"}
                    </li>
                  ))}
                </ul>
                {request.status === "rejected" && (
                  <p role="status" className="puf-rejection-reason">
                    <strong>Admin's reason:</strong> {request.rejectionReason || "No reason was provided."}
                  </p>
                )}
                {request.reviewedAt && (
                  <p className="puf-reviewed-time">
                    Reviewed {new Date(request.reviewedAt).toLocaleString()}
                  </p>
                )}
              </article>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default ProfileUpdateForm;